import { useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useMutation } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import {
  ArrowLeft,
  CheckCircle,
  Eye,
  EyeOff,
  KeyRound,
  Loader2,
  Lock,
  Shield,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { ApiHttpError, requestResetCode, verifyResetCode } from '@/api/auth'
import { defaultLocale } from '@/lib/locales'

const schema = z
  .object({
    email: z.string().min(1, 'Email requis').email('Email invalide'),
    code: z.string().regex(/^\d{6}$/, 'Le code doit contenir 6 chiffres'),
    password: z.string().min(8, 'Au moins 8 caractères'),
    confirm: z.string().min(1, 'Confirmation requise'),
  })
  .refine((d) => d.password === d.confirm, {
    path: ['confirm'],
    message: 'Les mots de passe ne correspondent pas',
  })

type Form = z.infer<typeof schema>

import { useDocumentTitle } from '@/hooks/useDocumentTitle'

export function ResetPasswordPage() {
  const { locale = defaultLocale } = useParams<{ locale: string }>()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  useDocumentTitle(t('resetPassword.pageTitle', 'Nouveau mot de passe'))

  const [showPwd, setShowPwd] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [success, setSuccess] = useState(false)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<Form>({
    resolver: zodResolver(schema),
    defaultValues: { email: '', code: '', password: '', confirm: '' },
  })

  useEffect(() => {
    const emailParam = searchParams.get('email')
    if (emailParam) setValue('email', emailParam)
  }, [searchParams, setValue])

  const resetMutation = useMutation({
    mutationFn: async (values: Form) => {
      await verifyResetCode(
        { email: values.email, code: values.code, newPassword: values.password },
        locale,
      )
    },
    onSuccess: () => {
      setSuccess(true)
      setTimeout(() => {
        navigate(`/${locale}/login?message=password_reset_success`, { replace: true })
      }, 3000)
    },
    onError: (error) => {
      if (error instanceof ApiHttpError) {
        if (error.status === 429) {
          toast.error(
            t('auth.resetPassword.tooManyAttempts') ||
              'Trop de tentatives. Veuillez patienter une minute.',
          )
          return
        }
        const data = error.data as { error?: string; detail?: string }
        toast.error(
          data?.error || data?.detail || t('auth.resetPassword.errorOccurred', 'Erreur'),
        )
        return
      }
      console.error('[ResetPassword]', error)
      toast.error(
        t('auth.resetPassword.connectionError', 'Erreur de connexion au serveur'),
      )
    },
  })

  const resendMutation = useMutation({
    mutationFn: async (email: string) => requestResetCode(email, locale),
    onSuccess: () => {
      toast.success(locale === 'en' ? 'New code sent!' : 'Nouveau code envoyé !')
    },
    onError: (error) => {
      if (error instanceof ApiHttpError && error.status === 429) {
        toast.error(
          t('auth.resetPassword.tooManyAttempts') ||
            'Trop de tentatives. Veuillez patienter une minute.',
        )
        return
      }
      toast.error(
        t('auth.resetPassword.errorOccurred') || "Impossible d'envoyer un nouveau code",
      )
    },
  })

  const onSubmit = handleSubmit((values) => resetMutation.mutate(values))
  const isSubmitting = resetMutation.isPending

  if (success) {
    return (
      <div className="min-h-screen grid md:grid-cols-2 bg-white text-slate-900">
        <div className="relative hidden md:flex items-center justify-center bg-[#0A192F] text-white p-10 overflow-hidden">
          <div
            className="absolute inset-0 opacity-10 pointer-events-none"
            style={{
              background:
                'radial-gradient(800px 400px at 50% 50%, #ffffff 0%, transparent 60%)',
            }}
          />
          <div className="relative z-10 text-center space-y-4">
            <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-12 h-12 text-green-400" />
            </div>
            <h1 className="text-3xl font-semibold">
              {t('auth.resetPassword.successTitle', 'Mot de passe réinitialisé !')}
            </h1>
            <p className="text-white/80 max-w-md">
              {t('auth.resetPassword.successMessage') ||
                'Votre mot de passe a été modifié. Vous pouvez vous reconnecter.'}
            </p>
          </div>
        </div>

        <div className="flex items-center justify-center p-6 md:p-10">
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4 }}
            className="w-full max-w-sm text-center"
          >
            <div className="md:hidden w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-2xl font-semibold mb-4">
              {t('auth.resetPassword.successTitle', 'Mot de passe réinitialisé !')}
            </h2>
            <p className="text-slate-600 mb-6">
              {t('auth.resetPassword.redirecting', 'Redirection vers la page de connexion...')}
            </p>
            <Button
              onClick={() => navigate(`/${locale}/login`)}
              className="w-full bg-[#0A192F] text-white py-2.5 rounded-lg font-medium hover:bg-slate-800"
            >
              {t('auth.resetPassword.loginNow', 'Se connecter maintenant')}
            </Button>
          </motion.div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white text-slate-900">
      <div className="relative hidden md:flex items-end bg-[#0A192F] text-white p-10 overflow-hidden">
        <div
          className="absolute inset-0 opacity-10 pointer-events-none"
          style={{
            background:
              'radial-gradient(800px 400px at 20% 10%, #ffffff 0%, transparent 60%)',
          }}
        />
        <div className="relative z-10 space-y-4">
          <div className="inline-flex items-center gap-2 rounded-full border border-white/20 px-3 py-1 text-xs text-white/80">
            <span className="h-1.5 w-1.5 rounded-full bg-white" />{' '}
            {t('auth.resetPassword.badge', 'Nouveau mot de passe')}
          </div>
          <h1 className="text-3xl font-semibold leading-tight">
            {t('auth.resetPassword.leftTitle', 'Créez un mot de passe sécurisé')}
          </h1>
          <p className="text-white/80 max-w-md">
            {t('auth.resetPassword.leftSubtitle') ||
              'Entrez le code reçu par email et choisissez un nouveau mot de passe fort.'}
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-10">
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.4 }}
          className="w-full max-w-sm"
        >
          <div className="mb-6 flex items-center gap-2">
            <img
              src="https://sdk.callizy.ca/assets/favicon_callizy.png"
              alt="Callizy"
              className="h-7 w-7"
            />
            <span className="font-semibold">Callizy</span>
          </div>

          <div className="mb-8">
            <div className="inline-flex items-center justify-center w-12 h-12 bg-slate-100 rounded-xl mb-4">
              <Shield className="w-6 h-6 text-slate-700" />
            </div>
            <h2 className="text-2xl font-semibold mb-2">
              {t('auth.resetPassword.title', 'Réinitialiser le mot de passe')}
            </h2>
            <p className="text-slate-600 text-sm">
              {t('auth.resetPassword.description') ||
                'Entrez le code reçu par email et votre nouveau mot de passe'}
            </p>
          </div>

          <form onSubmit={onSubmit} className="space-y-4" noValidate>
            <div>
              <Label htmlFor="email" className="text-slate-700">
                {t('auth.resetPassword.emailLabel', 'Adresse email')}
              </Label>
              <Input
                id="email"
                type="email"
                autoComplete="email"
                aria-invalid={!!errors.email}
                disabled={isSubmitting}
                className="mt-1"
                {...register('email')}
              />
              {errors.email && (
                <p className="mt-1 text-xs text-red-600">{errors.email.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="code" className="text-slate-700">
                {t('auth.resetPassword.codeLabel', 'Code de réinitialisation')}
              </Label>
              <div className="relative mt-1">
                <Input
                  id="code"
                  inputMode="numeric"
                  maxLength={6}
                  placeholder="123456"
                  aria-invalid={!!errors.code}
                  disabled={isSubmitting}
                  className="pr-10 text-center text-lg tracking-widest"
                  {...register('code', {
                    onChange: (e) => {
                      e.target.value = e.target.value.replace(/\D/g, '').slice(0, 6)
                    },
                  })}
                />
                <KeyRound className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              </div>
              {errors.code ? (
                <p className="mt-1 text-xs text-red-600">{errors.code.message}</p>
              ) : (
                <p className="mt-1 text-xs text-slate-500">
                  {t('auth.resetPassword.codeHint', 'Code à 6 chiffres reçu par email')}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="password" className="text-slate-700">
                {t('auth.resetPassword.newPasswordLabel', 'Nouveau mot de passe')}
              </Label>
              <div className="relative mt-1">
                <Input
                  id="password"
                  type={showPwd ? 'text' : 'password'}
                  autoComplete="new-password"
                  aria-invalid={!!errors.password}
                  disabled={isSubmitting}
                  className="pr-10"
                  {...register('password')}
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((v) => !v)}
                  disabled={isSubmitting}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPwd ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-xs text-red-600">{errors.password.message}</p>
              )}
            </div>

            <div>
              <Label htmlFor="confirm" className="text-slate-700">
                {t('auth.resetPassword.confirmPasswordLabel', 'Confirmer le mot de passe')}
              </Label>
              <div className="relative mt-1">
                <Input
                  id="confirm"
                  type={showConfirm ? 'text' : 'password'}
                  autoComplete="new-password"
                  aria-invalid={!!errors.confirm}
                  disabled={isSubmitting}
                  className="pr-10"
                  {...register('confirm')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm((v) => !v)}
                  disabled={isSubmitting}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showConfirm ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
              {errors.confirm && (
                <p className="mt-1 text-xs text-red-600">{errors.confirm.message}</p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 bg-[#0A192F] text-white py-2.5 rounded-lg font-medium hover:bg-slate-800 disabled:opacity-50"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  {t('auth.resetPassword.resetting', 'Réinitialisation...')}
                </>
              ) : (
                <>
                  <Lock className="w-4 h-4" />
                  {t('auth.resetPassword.resetButton', 'Réinitialiser le mot de passe')}
                </>
              )}
            </Button>

            <div className="text-center space-y-2 pt-4 border-t border-slate-200">
              <button
                type="button"
                onClick={() => {
                  const email = searchParams.get('email') || ''
                  if (email) resendMutation.mutate(email)
                }}
                disabled={resendMutation.isPending || !searchParams.get('email')}
                className="block w-full text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
              >
                {resendMutation.isPending
                  ? locale === 'en'
                    ? 'Sending...'
                    : 'Envoi...'
                  : t('auth.resetPassword.resendCode', 'Renvoyer un code')}
              </button>
              <button
                type="button"
                onClick={() => navigate(`/${locale}/login`)}
                className="inline-flex items-center text-sm text-slate-600 hover:text-slate-900"
              >
                <ArrowLeft className="w-4 h-4 mr-1" />
                {t('auth.resetPassword.backToLogin', 'Retour à la connexion')}
              </button>
            </div>
          </form>
        </motion.div>
      </div>
    </div>
  )
}
